/**
 * The app gate's next move, as a pure function of the OIDC state.
 *
 * Why this exists: oidc-client-ts only renews an access token that is ABOUT to expire. Load the page with
 * one that already has (the browser was closed for more than the ≈1h access-token lifetime) and it cancels
 * its renewal timer, react-oidc-context reports `isAuthenticated: false`, and a gate that redirects on that
 * alone sends the admin through the full hosted sign-in — password and emailed code — while a refresh token
 * valid for the rest of the week sits unused in localStorage. So a stored session with a refresh token is
 * refreshed silently first, and only a failed refresh (the 7-day token has expired, or was revoked) falls
 * through to the interactive sign-in.
 */
export type AuthStep = 'wait' | 'refresh' | 'redirect' | 'ready';

export interface AuthState {
  isLoading: boolean;
  /** react-oidc-context's in-flight navigator ('signinSilent', 'signinRedirect', …), if any. */
  activeNavigator: string | undefined;
  isAuthenticated: boolean;
  /** The stored (possibly expired) session carries a refresh token. */
  hasRefreshToken: boolean;
  /** A silent refresh was already tried on this page load and came back empty. */
  refreshFailed: boolean;
}

export function nextAuthStep(s: AuthState): AuthStep {
  if (s.isLoading || s.activeNavigator) return 'wait';
  if (s.isAuthenticated) return 'ready';
  if (s.hasRefreshToken && !s.refreshFailed) return 'refresh';
  return 'redirect';
}
